// Scenario mapping: missionId -> scenario data
// Used by ChapterDetailScreen to load the correct story content

import { egoCrisisScenario } from './storyLoader';
import { foundationGambleScenario } from './foundationGamble';
import { architectureUnderFireScenario } from './architectureUnderFire';
import { dataStormScenario } from './dataStorm';
import { cloudHeistScenario } from './cloudHeist';
import { neuralOverrideScenario } from './neuralOverride';
import { pipelineInfernoScenario } from './pipelineInferno';
import { mergeConflictScenario } from './mergeConflict';
import { deadlineParadoxScenario } from './deadlineParadox';
import { arabicModernScenario } from './arabicModern';
import { arabicLeadershipScenario } from './arabicLeadership';
import { arabicAIScenario } from './arabicAI';
import { arabicDigitalTransformationScenario } from './arabicDigitalTransformation';
import { arabicCybersecurityScenario } from './arabicCybersecurity';
import { quantumGambitScenario } from './quantumGambit';
import { learningMachineScenario } from './learningMachine';
import { inkProtocolScenario } from './inkProtocol';
import type { ScenarioChapter, SequenceItem } from './storyLoader';
import { getRuntimeScenario } from './scenarioStore';

export type { ScenarioChapter, SequenceItem };
export { loadRuntimeScenario, getRuntimeScenario, getRuntimeCharacter } from './scenarioStore';

export interface ScenarioMeta {
  id: string;
  missionId: number;
  title: string;
  certification: string;
  chapters: ScenarioChapter[];
}

/** Map missionId (from MissionsContext) to scenario data */
export const SCENARIO_MAP: Record<number, ScenarioMeta> = {
  1: {
    id: 'ego-crisis',
    missionId: 1,
    title: egoCrisisScenario.title,
    certification: egoCrisisScenario.certification,
    chapters: egoCrisisScenario.chapters,
  },
  2: {
    id: 'foundation-gamble',
    missionId: 2,
    title: foundationGambleScenario.title,
    certification: foundationGambleScenario.certification,
    chapters: foundationGambleScenario.chapters,
  },
  3: {
    id: 'data-storm',
    missionId: 3,
    title: dataStormScenario.title,
    certification: dataStormScenario.certification,
    chapters: dataStormScenario.chapters,
  },
  // Trial missions: 201 = Architecture Under Fire (AZ-305), 202 = Quantum Gambit, 203 = Ink Protocol
  102: {
    id: 'architecture-under-fire',
    missionId: 102,
    title: architectureUnderFireScenario.title,
    certification: architectureUnderFireScenario.certification,
    chapters: architectureUnderFireScenario.chapters,
  },
  201: {
    id: 'architecture-under-fire',
    missionId: 201,
    title: architectureUnderFireScenario.title,
    certification: architectureUnderFireScenario.certification,
    chapters: architectureUnderFireScenario.chapters,
  },
  202: {
    id: 'quantum-gambit',
    missionId: 202,
    title: quantumGambitScenario.title,
    certification: quantumGambitScenario.certification,
    chapters: quantumGambitScenario.chapters,
  },
  203: {
    id: 'merge-conflict',
    missionId: 203,
    title: mergeConflictScenario.title,
    certification: mergeConflictScenario.certification,
    chapters: mergeConflictScenario.chapters,
  },
  108: {
    id: 'arabic-modern',
    missionId: 108,
    title: arabicModernScenario.title,
    certification: arabicModernScenario.certification,
    chapters: arabicModernScenario.chapters,
  },
  109: {
    id: 'cloud-heist',
    missionId: 109,
    title: cloudHeistScenario.title,
    certification: cloudHeistScenario.certification,
    chapters: cloudHeistScenario.chapters,
  },
  110: {
    id: 'neural-override',
    missionId: 110,
    title: neuralOverrideScenario.title,
    certification: neuralOverrideScenario.certification,
    chapters: neuralOverrideScenario.chapters,
  },
  111: {
    id: 'pipeline-inferno',
    missionId: 111,
    title: pipelineInfernoScenario.title,
    certification: pipelineInfernoScenario.certification,
    chapters: pipelineInfernoScenario.chapters,
  },
  // Arabic missions (marketplace IDs 112-115)
  112: {
    id: 'arabic-leadership',
    missionId: 112,
    title: arabicLeadershipScenario.title,
    certification: arabicLeadershipScenario.certification,
    chapters: arabicLeadershipScenario.chapters,
  },
  113: {
    id: 'arabic-ai',
    missionId: 113,
    title: arabicAIScenario.title,
    certification: arabicAIScenario.certification,
    chapters: arabicAIScenario.chapters,
  },
  114: {
    id: 'arabic-digital-transformation',
    missionId: 114,
    title: arabicDigitalTransformationScenario.title,
    certification: arabicDigitalTransformationScenario.certification,
    chapters: arabicDigitalTransformationScenario.chapters,
  },
  115: {
    id: 'arabic-cybersecurity',
    missionId: 115,
    title: arabicCybersecurityScenario.title,
    certification: arabicCybersecurityScenario.certification,
    chapters: arabicCybersecurityScenario.chapters,
  },
  // New drama missions
  104: {
    id: 'quantum-gambit',
    missionId: 104,
    title: quantumGambitScenario.title,
    certification: quantumGambitScenario.certification,
    chapters: quantumGambitScenario.chapters,
  },
  105: {
    id: 'learning-machine',
    missionId: 105,
    title: learningMachineScenario.title,
    certification: learningMachineScenario.certification,
    chapters: learningMachineScenario.chapters,
  },
  106: {
    id: 'ink-protocol',
    missionId: 106,
    title: inkProtocolScenario.title,
    certification: inkProtocolScenario.certification,
    chapters: inkProtocolScenario.chapters,
  },
};

export function getScenarioForMission(missionId: number): ScenarioMeta | undefined {
  // Priorité au scénario chargé depuis le BLOB (store runtime) ; repli sur le
  // scénario bundlé local si le blob n'a pas (encore) été chargé.
  const runtime = getRuntimeScenario(missionId);
  if (runtime) {
    return {
      id: runtime.id,
      missionId,
      title: runtime.title,
      certification: runtime.certification,
      chapters: runtime.chapters,
    };
  }
  return SCENARIO_MAP[missionId];
}
